import core from '../core';
import { getQueryParamsObject } from '../../helpers/handle-url';
import events from '../../helpers/events';
import PageTypes from '../../helpers/page-types';
import { Glossary } from '../../helpers/Glossary';

const ModelImageUpdater = core.extend({
	constructor(opts) {
		this.data = opts.data || {};
		this.originalData = jQuery.extend({}, this.data);

		events(this);
	},

	setActiveProduct(product, preselection) {
		const diecut = this.getDefaultOption(false, product.productOptions, 'diecut'); // we don't pass preselection when we need not preselected but the first option
		const paper = this.getDefaultOption(preselection, product.productOptions, 'paper');
		const refinement = this.getDefaultOption(false, product.productOptions, 'refinement');
		const woodenBlock = this.getDefaultOption(preselection, product.productOptions, 'woodenBlock');
		const frame = this.getDefaultFrame(preselection, product.productOptions);
		const outerPaper = this.getDefaultOption(preselection, product.productOptions, 'outerPaper');
		const ribbon = this.getDefaultOption(preselection, product.productOptions, 'ribbon');

		_.extend(this.data, {
			format: product.format.slice(1),
			diecut: (diecut && diecut.slice(1)) || '00',
			wooden_block: (woodenBlock && woodenBlock.slice(2)) || (frame && frame.slice(2)) || '',
			outer_paper: outerPaper || '',
			paper: paper || '',
			lacquer: (refinement && refinement.slice(1)) || '00',
			ribbon: ribbon || '',
		});
	},

	getDefaultFrame(preselection, options) {
		options = options[`frames`] || [];

		return this.getFrameForCurrentFormat(preselection, options);
	},

	getFrameForCurrentFormat(preselection, frames) {
		let frame = frames[0];

		if (preselection && preselection['frame']) {
			const preSelectedFrametype = this.convertWoodenBlockValue(preselection['frame'].slice(2));
			frame =
				_.find(frames, (opt) => preSelectedFrametype === this.convertWoodenBlockValue(opt.key.slice(2))) ||
				frame;
		}

		return frame && frame.key;
	},

	getDefaultOption(preselection, options, optionName) {
		if (optionName === 'frame') {
			return this.getDefaultFrame(preselection, options);
		}

		options = options[`${optionName}s`] || [];

		const option =
			_.find(
				options,
				(opt) => preselection && preselection[optionName] && preselection[optionName] === opt.key
			) || options[0];

		return option && option.key;
	},

	refreshData(obj) {
		// dynamic data

		this.data.format = obj.format ? obj.format.slice(1, obj.format.length) : this.data.format || '';

		if (obj.color !== undefined && this.data.color !== obj.color) {
			this.publish('color:changed');
		}
		this.data.color = obj.color || this.data.color || '';

		this.data.lacquer = obj.lacquer ? obj.lacquer.slice(1, obj.lacquer.length) : this.data.lacquer || '';

		this.data.diecut = obj.diecut ? obj.diecut.slice(1, obj.diecut.length) : this.data.diecut || '';

		this.data.wooden_block = obj.wooden_block
			? obj.wooden_block.slice(2, obj.wooden_block.length)
			: this.data.wooden_block || '';

		this.data.paper = typeof obj.paper !== 'undefined' ? obj.paper : this.data.paper || '';

		this.data.outer_paper = typeof obj.outer_paper !== 'undefined' ? obj.outer_paper : this.data.outer_paper || '';

		this.data.box = typeof obj.box !== 'undefined' ? obj.box : this.data.box || '';

		const isRibbonDisabled = this.data.diecut === Glossary.NO_RIBBON_AND_TAG_DIECUT.slice(1);
		this.data.ribbon = typeof obj.ribbon !== 'undefined' && !isRibbonDisabled ? obj.ribbon : this.data.ribbon || '';
	},

	convertWoodenBlockValue(woodenBlock) {
		switch (woodenBlock) {
			case '00':
			case 'B':
				return 'B'; // without wooden frame for frames (WF00) or without wooden block for calendars (WB00)

			case '02':
			case '03':
			case '04':
			case '11':
			case '14':
			case '17':
			case 'C':
				return 'C'; // wire-o-binding WB02 or black frame WF02/WF03/WF04
			case '06':
			case '08':
			case '10':
			case '12':
			case '15':
			case '18':
			case 'D':
				return 'D'; // White thin Frames
			case '05':
			case '07':
			case '09':
			case '13':
			case '16':
			case '19':
			case 'E':
				return 'E'; // OAK FRAMES
			default:
				return '';
		}
	},

	getNewSrc(initialSrc, separator) {
		if (!initialSrc) {
			return;
		}

		if (this.data.mode === PageTypes.CP || this.data.mode === PageTypes.PDP) {
			return this.generateCompressedSrc(initialSrc, separator, this.data);
		}

		return this.generateExpandedSrc(initialSrc, this.data);
	},

	generateCompressedSrc(initialSrc, separator, data) {
		const srcArr = initialSrc.split(separator);

		if (srcArr.length <= 1 || initialSrc.match(/kam-in/gi) || initialSrc.match(/wed-in/gi)) {
			return initialSrc;
		}

		if (separator === '-') {
			const regexToDetectEnvelope = /\WEF(\d{2})/;
			const isEnvelope = regexToDetectEnvelope.test(srcArr[0]);

			const src = srcArr[srcArr.length - 1].split('?')[0];

			if (isEnvelope) {
				srcArr[srcArr.length - 1] = this.replaceOptionsInImageUrlPartForEnvelope(src, data);
			} else {
				srcArr[srcArr.length - 1] = this.replaceOptionsInImageUrlPart(src, data);
			}
		} else {
			srcArr[1] = this.data.color
				? this.data.color + srcArr[1].slice(this.data.color.length, srcArr[1].length)
				: srcArr[1];
		}

		return srcArr.join(separator);
	},

	replaceOptionsInImageUrlPart(imageUrlPart, data) {
		/**
		 * Matching groups:
		 *  1) format
		 *  2) color
		 *  3) refinement/lacquer
		 *  4) diecut
		 *  5) with photo (optional)
		 *  6) paper (optional)
		 *  7) image variants B/C e.g. for wooden block (optional)
		 *  8) outer paper (optional)
		 *  9) box (optional)
		 *  10) ribbon (optional)
		 *  11) locale (optional)
		 *  12) file extension
		 * @type {RegExp}
		 */
		const srcRegex =
			/^(\d{3})(\d{2})(\d{2})(\d{2})(\d?)((?:P\d{2})?)([BCDE]?)((?:OP\d{2})?)((?:B\d{2})?)((?:R\wL\d{2,3}C\d{2})?)((?:[a-z]{2}_[A-Z]{2})?)(\.jpg)$/;
		const srcMatch = srcRegex.exec(imageUrlPart);

		if (srcMatch === null) {
			return imageUrlPart;
		}

		//needs to be kept in sync with Shared/src/UrlBuilder/ImageUrlBuilder.php::PAPER_SKUS_TO_USE_IN_IMAGE_URL
		const usePaperInDynamicPart = ['P11', 'P15', 'P30', 'P31', 'P32', 'P50'].includes(data.paper);

		const dynamicPart =
			(data.format || srcMatch[1]) +
			(data.color ? data.color.replace(/\D/g, '') : data.noColors ? '' : srcMatch[2]) +
			(data.lacquer || srcMatch[3]) +
			(data.diecut || srcMatch[4]) +
			(data.photo || srcMatch[5]) +
			(usePaperInDynamicPart ? data.paper : '') +
			(data.wooden_block ? this.convertWoodenBlockValue(data.wooden_block) : srcMatch[7]) +
			(data.outer_paper || srcMatch[8]) +
			(data.box || srcMatch[9]) +
			(data.ribbon || '');
		const staticPart = (srcMatch[11] || 'de_DE') + srcMatch[12];

		return dynamicPart + staticPart;
	},

	replaceOptionsInImageUrlPartForEnvelope(imageUrlPart, data) {
		/**
		 * Matching groups:
		 *  1) color
		 *  2) locale (optional)
		 *  3) file extension
		 * @type {RegExp}
		 */
		const srcRegex = /^(\d{3})((?:[a-z]{2}_[A-Z]{2})?)(\.jpg)$/;
		const srcMatch = srcRegex.exec(imageUrlPart);

		if (srcMatch === null) {
			return imageUrlPart;
		}

		const dynamicPart = data.color ? data.color.replace(/\D/g, '') : data.noColors ? '' : srcMatch[1];
		const staticPart = (srcMatch[2] || 'de_DE') + srcMatch[3];

		return dynamicPart + staticPart;
	},

	generateExpandedSrc(initialSrc) {
		const srcArr = initialSrc.split('?');
		const data = getQueryParamsObject(srcArr[1]);

		for (const key in data) {
			data[key] = this.data[key] || data[key];
		}

		return `${srcArr[0]}?${decodeURIComponent(jQuery.param(data))}`;
	},
});

export default ModelImageUpdater;
